import React, { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import flagEn from '@/assets/images/flags/flag-english.svg'
import flagFr from '@/assets/images/flags/flag-french.svg'
import flagDe from '@/assets/images/flags/flag-german.svg'
import flagPl from '@/assets/images/flags/flag-polish.svg'
import { Icon } from '@/Atomic/Atoms/Icon'
import {
  ProfileMenu,
  ProfileMenuItemProps,
} from '@/Atomic/Organisms/ProfileMenu'
import { languageSwitch } from '@/utils/languageSwitch'

import './DXPProfileMenu.scss'

const FLAG_IMAGES: Record<string, string> = {
  en: flagEn,
  de: flagDe,
  fr: flagFr,
  pl: flagPl,
}

export const getFlag = (code: string): string => FLAG_IMAGES[code] || flagEn

interface LanguageConfig {
  code: string
  labelKey: string
}

const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  { code: 'en', labelKey: 'common.lang.en' },
  { code: 'de', labelKey: 'common.lang.de' },
  { code: 'fr', labelKey: 'common.lang.fr' },
  { code: 'pl', labelKey: 'common.lang.pl' },
]

interface UserAccount {
  email?: string
  name?: string
  picture?: string
  given_name?: string
  family_name?: string
}

export interface DXPProfileMenuProps {
  user: UserAccount
  onLogout: () => void
  className?: string
}

export const DXPProfileMenu: React.FC<DXPProfileMenuProps> = ({
  user,
  onLogout,
  className,
}) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.resolvedLanguage || 'en'

  const menuItems: ProfileMenuItemProps[] = useMemo(() => {
    const languageItems: ProfileMenuItemProps[] = SUPPORTED_LANGUAGES.map(
      (lang) => ({
        id: `lang-${lang.code}`,
        icon: (
          <img
            src={getFlag(lang.code)}
            alt={t(lang.labelKey)}
            className="dxp-profile-menu-flag"
          />
        ),
        label: t(lang.labelKey),
        isActive: lang.code === currentLanguage,
        variant: 'default' as const,
        onClick: () => languageSwitch(lang.code),
      }),
    )

    const logoutItem: ProfileMenuItemProps = {
      id: 'logout',
      icon: <Icon icon="logout" className="dxp-profile-menu-logout-icon" />,
      label: t('profile.menu.logOut'),
      variant: 'danger',
      onClick: onLogout,
    }

    return [...languageItems, logoutItem]
  }, [t, currentLanguage, onLogout])

  return <ProfileMenu user={user} menuItems={menuItems} className={className} />
}

export default DXPProfileMenu
